import type { ReactNode } from "react";
import { Link } from "react-router";
import { buttonClasses } from "@/components/design/button-classes";
import { PowerCollage } from "@/components/grow/PowerCollage";
import { TileArtwork } from "@/components/grow/TileArtwork";

// A featured method on Grow · Play: Power Quiz, Smart Analysis and the roleplay game, each a card of the same width
// in the featured grid (decision 61).

export function FeaturedCard({
  id,
  titleId,
  actionId,
  title,
  description,
  status,
  href,
  eyebrow,
  action,
  actionControl,
  imageSrc,
  powerCollage = false,
  anchor,
  hue,
}: {
  title: string;
  description: string;
  status: ReactNode;
  href?: string;
  eyebrow: ReactNode;
  action?: ReactNode;
  actionControl?: ReactNode;
  imageSrc?: string;
  powerCollage?: boolean;
  anchor?: string;
  hue: number;
  // Test anchors. Optional: only the questionnaire-group card is addressed by a
  // spec, so only that call site passes them - an id with no consumer is not
  // maintained (AGENTS.md, "Test anchors before tests").
  id?: string;
  titleId?: string;
  // The card root is a <section>, not a control: clicking it navigates nowhere.
  // The action link is the only way in, so a spec that opens the featured group
  // has to address it directly.
  actionId?: string;
}) {
  return (
    <section
      id={id}
      className="box grid min-h-48 overflow-hidden bg-surface @md:grid-cols-[176px_minmax(0,1fr)]"
    >
      <div className="flex min-h-40 items-center justify-center overflow-hidden p-4">
        {powerCollage ? (
          <PowerCollage className="aspect-square w-full rounded-md ring-1 ring-ink/10" />
        ) : (
          <TileArtwork
            imageSrc={imageSrc}
            hue={hue}
            variant="grid"
            className="aspect-square w-full rounded-md ring-1 ring-ink/10"
          />
        )}
      </div>
      <div className="flex min-w-0 flex-col p-5">
        <p className="smycles-field-label">{eyebrow}</p>
        <h2 id={titleId} className="mt-2 text-smycles-title font-medium">
          {title}
        </h2>
        {description ? (
          <p className="mt-2 max-w-[50ch] text-smycles-body leading-relaxed text-ink-muted">
            {description}
          </p>
        ) : null}
        <div className="mt-auto flex flex-wrap items-end justify-between gap-4 pt-4">
          <span className="text-smycles-meta text-ink-muted">{status}</span>
          {actionControl ?? (href ? (
            <Link
              id={actionId}
              to={href}
              data-assistant-anchor={anchor}
              className={buttonClasses({ variant: "secondary", size: "compact" })}
            >
              {action}
            </Link>
          ) : null)}
        </div>
      </div>
    </section>
  );
}
